import { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, CreditCard, Package, Heart, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import KrispButton from '../components/KrispButton';
import FloatingChips from '../components/FloatingChips';
import Footer from '../sections/Footer';

const PERKS = [
  { icon: Package, title: 'ORDER TRACKING', desc: 'Every pack from roaster to doorstep, once the store goes live.' },
  { icon: Heart, title: 'SAVED FLAVOURS', desc: 'Your stack, ready to reorder in one tap.' },
  { icon: CreditCard, title: 'SAVED PAYMENTS', desc: 'UPI, cards and wallets — added when the gateway is live.' },
];

export default function AccountPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reserve = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setBusy(true);
    try {
      await api.post('/waitlist', { email, interest: `EARLY ACCOUNT${name ? ` — ${name}` : ''}` });
      setDone(true);
      toast('ACCOUNT RESERVED. WE’LL EMAIL YOUR INVITE.');
    } catch { toast('THAT FUMBLED. TRY AGAIN.'); } finally { setBusy(false); }
  };

  return (
    <main data-testid="account-page" className="bg-cream min-h-screen">
      <section className="relative overflow-hidden bg-kyellow">
        <FloatingChips count={4} />
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-14 py-14 md:py-20 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <p className="font-cond text-xl tracking-[0.3em] text-cocoa/70">MY ACCOUNT</p>
            <h1 className="font-display text-[15vw] md:text-[7vw] leading-[0.85] text-cocoa mt-2">RESERVE YOUR<br />PLACE.</h1>
            <p className="text-cocoa/80 mt-5 max-w-md">Accounts unlock with the store. Reserve yours now and you’ll get the first login invite, early-access pricing and drop alerts.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-8">
              {PERKS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="rounded-2xl bg-cream border-2 border-cocoa p-4">
                  <span className="w-10 h-10 rounded-full bg-cocoa text-kyellow flex items-center justify-center"><Icon size={18} /></span>
                  <p className="font-cond font-bold text-sm tracking-wider text-cocoa mt-3">{title}</p>
                  <p className="text-xs text-cocoa/70 mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] bg-cocoa text-cream border-2 border-cocoa p-8 shadow-[10px_10px_0_#E11D24]">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-full bg-kyellow text-cocoa flex items-center justify-center"><User size={22} /></span>
              <div><p className="font-display text-3xl leading-none">EARLY ACCESS ACCOUNT</p><p className="font-cond text-[10px] tracking-[0.3em] text-cream/60 mt-1">LOGIN OPENS WITH THE STORE</p></div>
            </div>
            {done ? (
              <div className="mt-8 rounded-2xl border-2 border-kyellow bg-kyellow/10 p-6 text-center" data-testid="account-reserved">
                <p className="font-display text-3xl text-kyellow">YOU’RE RESERVED ✓</p>
                <p className="text-sm text-cream/70 mt-2">Your invite lands the day the store opens.</p>
              </div>
            ) : (
              <form onSubmit={reserve} className="mt-8 space-y-3" data-testid="account-form">
                <label className="block">
                  <span className="font-cond text-[10px] tracking-[0.3em] text-cream/60 flex items-center gap-2"><User size={11} /> NAME</span>
                  <input data-testid="account-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="k-input mt-1.5" />
                </label>
                <label className="block">
                  <span className="font-cond text-[10px] tracking-[0.3em] text-cream/60 flex items-center gap-2"><Mail size={11} /> EMAIL</span>
                  <input data-testid="account-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="k-input mt-1.5" />
                </label>
                <label className="block opacity-60">
                  <span className="font-cond text-[10px] tracking-[0.3em] text-cream/60 flex items-center gap-2"><Lock size={11} /> PASSWORD</span>
                  <input data-testid="account-password" type="password" disabled placeholder="Set when login opens" className="k-input mt-1.5" />
                </label>
                <KrispButton testid="account-submit" type="submit" disabled={busy} className="w-full mt-2">{busy ? 'RESERVING...' : 'RESERVE MY ACCOUNT →'}</KrispButton>
              </form>
            )}
            <Link to="/payments" data-testid="account-payments-link" className="mt-6 flex items-center justify-between rounded-2xl border-2 border-cream/20 px-4 py-3 hover:border-kyellow transition-colors">
              <span className="font-cond font-bold text-sm tracking-wider flex items-center gap-2"><CreditCard size={16} className="text-kyellow" /> PAYMENTS & CHECKOUT</span><ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
